import { EmptyState } from "@/components/common/EmptyState";
import { ScreenHeader } from "@/components/common/ScreenHeader";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { VehicleCard } from "@/components/vehicle/VehicleCard";
import { BrandColors, Colors } from "@/constants/theme";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { useVehicles } from "@/hooks/queries/use-vehicles-query";
import { queryKeys } from "@/lib/query-keys";
import { Vehicle } from "@/lib/services/vehicles";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "expo-router";
import React, { useCallback, useMemo } from "react";
import {
  FlatList,
  RefreshControl,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

interface SummaryCardProps {
  icon: React.ComponentProps<typeof Ionicons>["name"];
  value: number;
  label: string;
  isDark: boolean;
  textColor: string;
  desktop?: boolean;
  borderColor?: string;
}

const SummaryCard = ({
  icon,
  value,
  label,
  isDark,
  textColor,
  desktop = false,
  borderColor,
}: SummaryCardProps) =>
  desktop ? (
    <View
      style={[
        styles.desktopSummaryCard,
        {
          backgroundColor: isDark ? "#1B2436" : BrandColors.white,
          borderColor,
        },
      ]}
    >
      <Ionicons name={icon} size={20} color={BrandColors.primary} />
      <View>
        <ThemedText
          style={[styles.desktopSummaryValue, { color: textColor }]}
          type="defaultSemiBold"
        >
          {value}
        </ThemedText>
        <ThemedText style={styles.summaryLabel} type="extraSmall">
          {label}
        </ThemedText>
      </View>
    </View>
  ) : (
    <View style={[styles.summaryCard, isDark && styles.summaryCardDark]}>
      <View style={styles.summaryIconWrap}>
        <Ionicons name={icon} size={18} color={BrandColors.primary} />
      </View>
      <ThemedText
        style={[styles.summaryValue, { color: textColor }]}
        type="defaultSemiBold"
      >
        {value}
      </ThemedText>
      <ThemedText style={styles.summaryLabel} type="extraSmall">
        {label}
      </ThemedText>
    </View>
  );

export default function VehiclesScreen() {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const theme = Colors[colorScheme ?? "light"];
  const desktop = useDesktopLayout();
  const qc = useQueryClient();

  const {
    data: vehicles = [],
    isLoading,
    isRefetching,
    refetch,
  } = useVehicles();

  const availableCount = useMemo(
    () => vehicles.filter((vehicle) => vehicle.is_available).length,
    [vehicles],
  );

  const onRefresh = () => {
    qc.invalidateQueries({ queryKey: queryKeys.vehicles.list() });
    refetch();
  };

  const renderVehicle = useCallback(
    ({ item }: { item: Vehicle }) => (
      <VehicleCard
        vehicle={item}
        backgroundColor={isDark ? "#1B2436" : BrandColors.white}
        borderColor={theme.border}
        onPress={() => router.push(`/vehicles/${item.id}`)}
      />
    ),
    [isDark, theme.border],
  );

  const keyExtractor = useCallback((item: Vehicle) => item.id.toString(), []);

  const listHeader = (
    <View style={styles.summaryRow}>
      <SummaryCard
        icon="car-sport-outline"
        value={vehicles.length}
        label="Total Vehicles"
        isDark={isDark}
        textColor={theme.text}
      />
      <SummaryCard
        icon="checkmark-circle-outline"
        value={availableCount}
        label="Available"
        isDark={isDark}
        textColor={theme.text}
      />
    </View>
  );

  const emptyState = (
    <EmptyState
      icon="car-sport-outline"
      title="No vehicles registered"
      subtitle="Add a vehicle to start accepting jobs."
      actionLabel="Add Vehicle"
      onAction={() => router.push("/vehicles/create")}
    />
  );

  if (desktop) {
    const cardColor = isDark ? "#1B2436" : BrandColors.white;

    return (
      <View
        style={[
          styles.safeArea,
          {
            backgroundColor: isDark
              ? theme.background
              : BrandColors.backgroundGray,
          },
        ]}
      >
        {isLoading ? (
          <Loader inline />
        ) : (
          <ScrollView
            contentContainerStyle={styles.desktopScroll}
            refreshControl={
              <RefreshControl refreshing={isRefetching} onRefresh={onRefresh} />
            }
          >
            <View style={styles.desktopHeader}>
              <ThemedText type="subtitle" style={styles.desktopTitle}>
                My Vehicles
              </ThemedText>
              <TouchableOpacity
                accessibilityLabel="add-vehicle-button"
                accessibilityRole="button"
                accessibilityHint="Add a new vehicle"
                onPress={() => router.push("/vehicles/create")}
                style={[
                  styles.desktopAddButton,
                  { backgroundColor: cardColor, borderColor: theme.border },
                ]}
              >
                <Ionicons name="add" size={22} color={theme.text} />
              </TouchableOpacity>
            </View>

            {vehicles.length === 0 ? (
              emptyState
            ) : (
              <>
                <View style={styles.desktopSummaryRow}>
                  <SummaryCard
                    icon="car-sport-outline"
                    value={vehicles.length}
                    label="Total Vehicles"
                    isDark={isDark}
                    textColor={theme.text}
                    desktop
                    borderColor={theme.border}
                  />
                  <SummaryCard
                    icon="time-outline"
                    value={availableCount}
                    label="Available"
                    isDark={isDark}
                    textColor={theme.text}
                    desktop
                    borderColor={theme.border}
                  />
                </View>

                <View style={styles.desktopGrid}>
                  {vehicles.map((vehicle) => (
                    <View key={vehicle.id} style={styles.desktopGridItem}>
                      <VehicleCard
                        vehicle={vehicle}
                        backgroundColor={cardColor}
                        borderColor={theme.border}
                        onPress={() => router.push(`/vehicles/${vehicle.id}`)}
                        desktop
                      />
                    </View>
                  ))}
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel="Add another vehicle"
                    onPress={() => router.push("/vehicles/create")}
                    style={[
                      styles.desktopGridItem,
                      styles.desktopAddTile,
                      { borderColor: theme.border },
                    ]}
                  >
                    <Ionicons name="add" size={24} color={theme.icon} />
                    <ThemedText style={{ color: theme.icon }}>
                      Add another Vehicle
                    </ThemedText>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </ScrollView>
        )}
      </View>
    );
  }

  return (
    <SafeAreaView
      style={[styles.safeArea, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <ScreenHeader
        title="My Vehicles"
        rightComponent={
          <TouchableOpacity
            accessibilityLabel="add-vehicle-button"
            accessibilityRole="button"
            accessibilityHint="Add a new vehicle"
            onPress={() => router.push("/vehicles/create")}
          >
            <Ionicons name="add-circle" size={24} color={theme.primary} />
          </TouchableOpacity>
        }
      />
      {isLoading ? (
        <Loader inline />
      ) : (
        <View style={styles.container}>
          <FlatList
            data={vehicles}
            renderItem={renderVehicle}
            keyExtractor={keyExtractor}
            contentContainerStyle={styles.listContent}
            ListHeaderComponent={vehicles.length > 0 ? listHeader : undefined}
            refreshControl={
              <RefreshControl refreshing={isRefetching} onRefresh={onRefresh} />
            }
            ListEmptyComponent={emptyState}
          />
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  listContent: {
    padding: 16,
    paddingTop: 8,
  },
  summaryRow: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 16,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: BrandColors.backgroundGray,
    borderRadius: 16,
    padding: 16,
    gap: 6,
  },
  summaryCardDark: {
    backgroundColor: "#1B2436",
  },
  summaryIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: BrandColors.primaryLight,
    alignItems: "center",
    justifyContent: "center",
  },
  summaryValue: {
    fontSize: 28,
    lineHeight: 32,
    color: BrandColors.charcoal,
  },
  summaryLabel: {
    color: BrandColors.mediumGray,
  },
  desktopScroll: {
    padding: 24,
    paddingBottom: 48,
  },
  desktopHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
    marginBottom: 24,
  },
  desktopTitle: {
    fontSize: 22,
    fontWeight: "700",
  },
  desktopAddButton: {
    position: "absolute",
    right: 0,
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  desktopSummaryRow: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 16,
  },
  desktopSummaryCard: {
    width: 184,
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    paddingHorizontal: 18,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  desktopSummaryValue: {
    fontSize: 18,
    lineHeight: 24,
  },
  desktopGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  desktopGridItem: {
    flexBasis: "30%",
    flexGrow: 1,
    maxWidth: "32%",
  },
  desktopAddTile: {
    minHeight: 236,
    borderWidth: 1,
    borderStyle: "dashed",
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  emptyState: {
    alignItems: "center",
    paddingTop: 80,
  },
  emptyText: {
    fontSize: 16,
    color: "#666",
    marginTop: 16,
  },
  addButton: {
    marginTop: 20,
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: BrandColors.primary,
    borderRadius: 8,
  },
  addButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
});
